import type { RouteUnavailability } from "../../../api/connectorStatus";
import type { ThreadState } from "../../../api/threads/threadDecoders";
import type { ActionDecision } from "../page/viewerActions";

/**
 * What the composer tells the user before they send (threads.md T-6, T-8; FR-THREAD-4, 7 as
 * amended by R2): whether they may write, and whether the agent answers now or later. Whether
 * the viewer may post, and on whose agent a reply runs, are module C's decisions (W-22 slice 2,
 * the viewer section), not rules repeated here. Pure.
 */
export interface ComposerFacts {
  readonly threadState: ThreadState;
  /** Module C's decision on posting a message for this viewer. */
  readonly posting: ActionDecision;
  /** Module C's decision on asking the agent: refused with `not_own_route` when the reply runs on another's agent. */
  readonly replying: ActionDecision;
  readonly connectorAttached: boolean;
  readonly connectorOnline: boolean;
  readonly routeChosen: boolean;
  /** Module C's answer for the Run's route on the host's connector (CP-11): why a reply can't run now, or `null`. */
  readonly routeUnavailableBecause: RouteUnavailability | null;
  /** Module C lists an open turn (W-22): it ends with its result entry. */
  readonly turnOpen: boolean;
  /** The open turn waits for its tool's approval on the host's PC (TR-B1-01). */
  readonly openTurnAwaitsApproval: boolean;
  /** The open turn waits for a free slot on the host's PC (X-2). */
  readonly openTurnWaitsForSlot: boolean;
}

export type ComposerAvailability =
  | { readonly canWrite: true }
  /** `savedNotHost`: only the host appends while saved; others fork (T-8). `missingPermission`: module A lets the viewer read, not write; its remedy says who can change that (GS-8). */
  | { readonly canWrite: false; readonly reason: "closed" | "savedNotHost" | "missingPermission" };

/** Why the agent will not answer a message at once; one it takes now needs no word. */
export type AgentOutlook =
  /** Another person's PC runs this Thread: the message waits until the writer takes it over and runs it on their own agent. */
  | "runsWhenYouHost"
  /** The agent is working; the message joins its next turn (no steering, T-6). */
  | "joinsNextTurn"
  /** A tool of the open turn waits for its approval (TR-B1-01); the message joins the turn after it. */
  | "joinsAfterApproval"
  /** The open turn waits for a free slot on the host's PC (X-2); the message joins the turn after it. */
  | "joinsAfterSlot"
  /** No live connector: the agent answers once the host's connector is back. */
  | "waitsForHost"
  /** The host has not chosen an agent and model yet. */
  | "needsRoute"
  /** The host's connector can't run the Run's route now, for module C's reason: the agent answers once that is fixed. */
  | { readonly cannotRun: RouteUnavailability };

export interface ComposerOutlook {
  readonly availability: ComposerAvailability;
  /** `null` when the user cannot write, or nothing needs saying. */
  readonly agentOutlook: AgentOutlook | null;
  /** A turn is open: show it as working, and offer to cancel. */
  readonly agentBusy: boolean;
}

export function describeComposer(facts: ComposerFacts): ComposerOutlook {
  const agentBusy = facts.turnOpen;
  if (!facts.posting.allowed) {
    return { availability: { canWrite: false, reason: unavailable(facts.posting) }, agentOutlook: null, agentBusy };
  }
  return { availability: { canWrite: true }, agentOutlook: agentOutlookOf(facts, agentBusy), agentBusy };
}

function unavailable(posting: ActionDecision): "closed" | "savedNotHost" | "missingPermission" {
  if (posting.refusal === "saved_not_host") return "savedNotHost";
  return posting.refusal === "missing_permission" ? "missingPermission" : "closed";
}

function agentOutlookOf(facts: ComposerFacts, agentBusy: boolean): AgentOutlook | null {
  if (facts.replying.refusal === "not_own_route") {
    return "runsWhenYouHost";
  }
  if (!facts.routeChosen) {
    return "needsRoute";
  }
  if (facts.threadState !== "live" || !facts.connectorAttached || !facts.connectorOnline) {
    return "waitsForHost";
  }
  if (facts.routeUnavailableBecause !== null) return { cannotRun: facts.routeUnavailableBecause };
  if (!agentBusy) return null;
  if (facts.openTurnAwaitsApproval) return "joinsAfterApproval";
  return facts.openTurnWaitsForSlot ? "joinsAfterSlot" : "joinsNextTurn";
}
